/**
 * T4-07 — the renderer half of reconciling an unconfirmed desktop BlockChyp
 * dispatch (`pc:payments:reconcile-dispatch`).
 *
 * Two jobs, both deliberately narrow:
 *
 * 1. **Observe.** The two desktop charge paths (`payments.electron.ts` and
 *    `payments-lan.electron.ts`) hand every `charge` envelope to
 *    `noteElectronChargeEnvelope`. A `PAYMENT_UNRESOLVED_DISPATCH` refusal
 *    naming a stuck attempt (or an unreadable journal) is published as a
 *    `blocked` event; an accepted charge publishes `cleared` for its terminal.
 *    Observing never changes what the charge path throws or returns, so the
 *    engines' FAILED/UNKNOWN classification is untouched. `terminal-busy` is
 *    not published: that charge is still running, there is nothing to settle.
 * 2. **Act, only when a person asks.** A gateway status read, main's
 *    `resolve-dispatch`, and `accept-journal-loss`, each returning a typed
 *    result instead of throwing, carrying main's fixed refusal sentence and
 *    `details.reason` — never a raw error.
 */
import type { BridgeResult, ChannelResponse } from 'electron-shared/bridge-types';

import { paymentsBridge } from './bridge';
import type {
  DesktopDispatchEvent,
  DesktopDispatchReconciler,
  DesktopDispatchGatewayRead,
  DesktopDispatchReconcileResult,
} from '../services/types';

type Listener = (event: DesktopDispatchEvent) => void;

const listeners = new Set<Listener>();

const GATEWAY_STATUS_TIMEOUT_SECONDS = 15;

function publish(event: DesktopDispatchEvent): void {
  listeners.forEach((listener) => {
    try {
      listener(event);
    } catch {
      // A listener must never break the charge path that published the event.
    }
  });
}

/** What the refused/accepted charge was aimed at. */
export interface ElectronChargeContext {
  readonly terminalName: string;
  readonly test: boolean;
}

/**
 * Called by the desktop charge paths with the raw `charge` envelope, before
 * they settle it. Pure observation: never throws, never alters the result.
 */
export function noteElectronChargeEnvelope(
  result: BridgeResult<unknown>,
  context: ElectronChargeContext
): void {
  try {
    if (result.ok) {
      publish({ type: 'cleared', terminalName: context.terminalName });
      return;
    }
    if (result.error.code !== 'PAYMENT_UNRESOLVED_DISPATCH') return;
    const details = result.error.details ?? {};
    const observedAt = new Date().toISOString();

    if (
      details.reason === 'journal-unreadable' ||
      details.reason === 'journal-loss-terminal-unconfirmed'
    ) {
      publish({
        type: 'blocked',
        block: {
          reason: details.reason,
          transactionRef: null,
          markerState: null,
          terminalName: context.terminalName,
          test: context.test,
          observedAt,
        },
      });
      return;
    }
    if (details.reason === 'unresolved-dispatch' && details.blockingTransactionRef) {
      publish({
        type: 'blocked',
        block: {
          reason: 'unresolved-dispatch',
          transactionRef: details.blockingTransactionRef,
          markerState: details.state ?? null,
          terminalName: context.terminalName,
          test: context.test,
          observedAt,
        },
      });
    }
  } catch {
    // Observation is best effort by contract.
  }
}

function failureFrom(error: unknown): { code: string; message: string } {
  return {
    code: 'BRIDGE_UNAVAILABLE',
    message:
      error instanceof Error && error.name === 'ElectronBridgeUnavailableError'
        ? 'The desktop payment service is not available right now.'
        : 'The desktop payment service did not answer.',
  };
}

async function readGatewayStatus(
  transactionRef: string,
  test: boolean
): Promise<DesktopDispatchGatewayRead> {
  try {
    const result = await paymentsBridge().transactionStatus({
      transactionRef,
      test,
      timeout: GATEWAY_STATUS_TIMEOUT_SECONDS,
    });
    if (!result.ok) return { ok: false, code: result.error.code, message: result.error.message };
    const { value } = result;
    return {
      ok: true,
      status: {
        success: value.success,
        approved: value.approved,
        status: value.status,
        responseDescription: value.responseDescription,
        error: value.error,
        requestedAmount: value.requestedAmount,
        authorizedAmount: value.authorizedAmount,
        timestamp: value.timestamp,
      },
    };
  } catch (error: unknown) {
    return { ok: false, ...failureFrom(error) };
  }
}

function reconcileResult(
  result: BridgeResult<ChannelResponse<'pc:payments:reconcile-dispatch'>>
): DesktopDispatchReconcileResult {
  if (result.ok) {
    return {
      ok: true,
      resolvedBy: result.value.resolvedBy,
      approved: result.value.approved,
      salvagedEntries: result.value.salvagedEntries,
    };
  }
  return {
    ok: false,
    code: result.error.code,
    reason: result.error.details?.reason ?? null,
    message: result.error.message,
  };
}

async function resolveDispatch(
  transactionRef: string,
  operatorOutcome: 'not-charged' | 'charged'
): Promise<DesktopDispatchReconcileResult> {
  try {
    return reconcileResult(
      await paymentsBridge().reconcileDispatch({
        action: 'resolve-dispatch',
        transactionRef,
        operatorOutcome,
      })
    );
  } catch (error: unknown) {
    return { ok: false, reason: null, ...failureFrom(error) };
  }
}

async function acceptJournalLoss(terminalName?: string): Promise<DesktopDispatchReconcileResult> {
  try {
    return reconcileResult(
      await paymentsBridge().reconcileDispatch({
        action: 'accept-journal-loss',
        // Only sent to confirm one terminal after a loss (T4-N2).
        ...(terminalName ? { terminalName } : {}),
      })
    );
  } catch (error: unknown) {
    return { ok: false, reason: null, ...failureFrom(error) };
  }
}

const reconciler: DesktopDispatchReconciler = {
  subscribe(listener) {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
  readGatewayStatus,
  resolveDispatch,
  acceptJournalLoss,
};

/** The desktop reconciler. Stateless apart from the listener set; shared. */
export function electronDispatchReconciler(): DesktopDispatchReconciler {
  return reconciler;
}
